import React, { useState, useRef, useEffect, useMemo, useId } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { UserIcon } from "@heroicons/react/24/outline";
import { AuthMe } from "../types/auth";
import ProBadge from "./ProBadge";
import { companyRoute } from "../utils/routeHelpers";
import { A } from "../styles/appleClasses";
import { UI_LANGUAGE_OPTIONS, resolveUiLanguage } from "../i18n/languages";
import { setUiLanguage } from "../i18n";

// Inlays (new behavior)
import Profile from "../pages/Profile";
import ChangePassword from "../pages/ChangePassword";
import ChangeAvatar from "../pages/ChangeAvatar";
import AutoSignOut from "../pages/AutoSignOut";

type AccountMenuProps = {
  user: AuthMe;
  onAvatarChange?: (style: string, seed: string) => void;
  onLogout?: () => void;

  // optional: lets parent refresh user data after profile update
  onProfileUpdated?: (patch: { full_name?: string; email?: string }) => void;
};

type ActiveInlay = null | "profile" | "password" | "avatar" | "timeout";

export default function AccountMenu({
  user,
  onAvatarChange,
  onLogout,
  onProfileUpdated,
}: AccountMenuProps) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [activeInlay, setActiveInlay] = useState<ActiveInlay>(null);

  // Local display cache so the menu reflects updates immediately
  const [displayName, setDisplayName] = useState(user.full_name || "");
  const [displayEmail, setDisplayEmail] = useState(user.email || "");
  const [displayAvatarStyle, setDisplayAvatarStyle] = useState(
    user.avatar_style || "adventurer"
  );
  const [displayAvatarSeed, setDisplayAvatarSeed] = useState(
    user.avatar_seed || user.full_name || "Ricebook"
  );

  useEffect(() => {
    setDisplayName(user.full_name || "");
    setDisplayEmail(user.email || "");
    setDisplayAvatarStyle(user.avatar_style || "adventurer");
    setDisplayAvatarSeed(user.avatar_seed || user.full_name || "Ricebook");
  }, [user.full_name, user.email, user.avatar_style, user.avatar_seed]);

  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const internalMouseDownRef = useRef(false);
  const panelId = useId();
  const navigate = useNavigate();
  const { company_id: paramCompanyId } = useParams<{ company_id?: string }>();

  const isPro = user?.is_user_pro === true;

  // Close dropdown on outside mousedown without changing focus.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", onDoc);
      return () => {
        document.removeEventListener("mousedown", onDoc);
      };
    }
  }, [open]);

  const avatarUrl = useMemo(() => {
    return `https://api.dicebear.com/7.x/${displayAvatarStyle}/svg?seed=${encodeURIComponent(
      displayAvatarSeed
    )}`;
  }, [displayAvatarStyle, displayAvatarSeed]);

  const storedCompanyId =
    localStorage.getItem("current_company_id") ||
    sessionStorage.getItem("current_company_id") ||
    "";

  const companyId =
    paramCompanyId ||
    (user.company_id != null ? String(user.company_id) : undefined) ||
    (storedCompanyId || undefined);

  const closeInlay = () => setActiveInlay(null);

  function handleLogoutClick() {
    localStorage.removeItem("access_token");
    sessionStorage.removeItem("access_token");
    onLogout ? onLogout() : navigate("/login");
  }

  const itemBtn =
    `w-full text-left px-3 py-2 rounded-xl ${A.body} ` +
    `hover:bg-gray-50 transition ` +
    `focus:outline-none focus:ring-2 focus:ring-appleblueD`;

  return (
    <>
      <div
        className="relative inline-block text-left"
        ref={menuRef}
        onKeyDown={(event) => {
          if (open && event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            setOpen(false);
            triggerRef.current?.focus();
          }
        }}
        onMouseDownCapture={() => {
          internalMouseDownRef.current = true;
          window.setTimeout(() => {
            internalMouseDownRef.current = false;
          }, 0);
        }}
        onBlur={(event) => {
          if (
            event.relatedTarget &&
            event.currentTarget.contains(event.relatedTarget)
          ) {
            return;
          }
          if (internalMouseDownRef.current) {
            return;
          }
          setOpen(false);
        }}
      >
        {/* Avatar button */}
        <button
          type="button"
          ref={triggerRef}
          className={`${A.iconBtn} rounded-full overflow-hidden inline-flex items-center justify-center bg-white border border-gray-200
            hover:bg-gray-50 transition
            focus:outline-none focus:ring-2 focus:ring-appleblueD`}
          title={t("accountMenu.account")}
          onClick={(event) => {
            if (!open) event.currentTarget.focus();
            setOpen((o) => !o);
          }}
          aria-label={t("accountMenu.account")}
          aria-controls={panelId}
          aria-expanded={open}
        >
          {displayAvatarSeed ? (
            <img
              src={avatarUrl}
              alt={t("accountMenu.user")}
              className="w-full h-full object-cover"
            />
          ) : (
            <UserIcon className="w-6 h-6 text-gray-500" />
          )}
        </button>

        {/* Dropdown */}
          <div
            id={panelId}
            hidden={!open}
            className="absolute right-0 mt-2 w-72 bg-white border border-gray-200 rounded-2xl shadow-md z-50 p-2 min-w-[14rem]"
          >
            {open && (
              <>
            {/* Header */}
            <div className="px-2 py-3 border-b border-gray-200 relative">
              <div className="flex items-center gap-3">
                <img
                  src={avatarUrl}
                  alt={t("accountMenu.user")}
                  className="w-12 h-12 rounded-full border border-gray-200 object-cover"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-gray-700 truncate">
                    {displayName || "—"}
                  </div>
                  <div className={`${A.label} truncate`}>{displayEmail || "—"}</div>
                </div>
              </div>

              <div className="absolute top-3 right-3">
                <ProBadge show={isPro} size="xs" />
              </div>
            </div>

            {/* Items */}
            <button
              onClick={() => {
                setOpen(false);
                setActiveInlay("profile");
              }}
              className={itemBtn}
            >
              {t("accountMenu.profile")}
            </button>

            <button
              onClick={() => {
                setOpen(false);
                setActiveInlay("password");
              }}
              className={itemBtn}
            >
              {t("accountMenu.changePassword")}
            </button>

            <button
              onClick={() => {
                setOpen(false);
                setActiveInlay("avatar");
              }}
              className={itemBtn}
            >
              {t("accountMenu.changeAvatar")}
            </button>

            <button
              onClick={() => {
                setOpen(false);
                setActiveInlay("timeout");
              }}
              className={itemBtn}
            >
              {t("accountMenu.autoSignOut")}
            </button>

            <div
              className="px-3 py-0.5 text-[13px] font-normal text-gray-700"
              onMouseDown={(event) => {
                if (UI_LANGUAGE_OPTIONS.length <= 1) {
                  event.preventDefault();
                }
              }}
            >
              <select
                aria-label={t("accountMenu.language")}
                value={resolveUiLanguage(i18n.resolvedLanguage ?? i18n.language)}
                disabled={UI_LANGUAGE_OPTIONS.length <= 1}
                onChange={(event) => {
                  void setUiLanguage(event.currentTarget.value);
                }}
                className="w-full h-8 rounded-xl border border-gray-200 px-2.5 text-[13px] font-normal text-gray-700 focus:outline-none focus:ring-2 focus:ring-appleblue/20 focus:border-appleblue/50 disabled:pointer-events-none disabled:cursor-default disabled:bg-white disabled:text-gray-700 disabled:opacity-100 disabled:appearance-none"
              >
                {UI_LANGUAGE_OPTIONS.map(({ language, nativeLabel }) => (
                  <option key={language} value={language}>{nativeLabel}</option>
                ))}
              </select>
            </div>

            <div className="my-2 border-t border-gray-200" />

            <button
              onClick={() => {
                handleLogoutClick();
                setOpen(false);
              }}
              className={itemBtn}
            >
              {t("accountMenu.logOut")}
            </button>
              </>
            )}
          </div>
      </div>

      {/* Inlays (SaleEntry-style overlay components) */}
      {activeInlay === "profile" && (
        <Profile
          mode="inlay"
          initialUser={user}
          onClose={closeInlay}
          onUpdated={(patch) => {
            if (patch.full_name != null) setDisplayName(patch.full_name);
            if (patch.email != null) setDisplayEmail(patch.email);
            onProfileUpdated?.(patch);
          }}
        />
      )}

      {activeInlay === "password" && (
        <ChangePassword mode="inlay" onClose={closeInlay} />
      )}

      {activeInlay === "avatar" && (
        <ChangeAvatar
          mode="inlay"
          initialStyle={displayAvatarStyle}
          initialSeed={displayAvatarSeed}
          onClose={closeInlay}
          onSave={(style, seed) => {
            onAvatarChange?.(style, seed);
            setDisplayAvatarStyle(style);
            setDisplayAvatarSeed(seed);
            closeInlay();
          }}
        />
      )}

      {activeInlay === "timeout" && (
        <AutoSignOut
          mode="inlay"
          onClose={closeInlay}
          onTokenUpdated={(t) => {
            if (t) localStorage.setItem("access_token", t);
          }}
        />
      )}
    </>
  );
}
